import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { SupportedLanguage } from "@pimo/pimo-l10n";
import { useState } from "react";

import { BillingAddress } from "../types/entities";
import {
  BillingAddressDraft,
  BillingAddressForm,
} from "./billing-address-form";

export interface BillingAddressModalState {
  isOpen: boolean;
  billingAddress?: BillingAddress | null;
  isSubmitting?: boolean;
  errorMessage?: string;
  language: SupportedLanguage;
}

export type BillingAddressModalEvent =
  | "close-billing-address-modal"
  | "submit-billing-address";

export interface SubmitBillingAddressPayload {
  billingAddress: BillingAddressDraft;
}

export const BillingAddressModal: PimoReactComponent<
  BillingAddressModalState,
  BillingAddressModalEvent,
  SubmitBillingAddressPayload
> = ({
  fireEvent,
  isOpen,
  billingAddress,
  isSubmitting,
  errorMessage,
  language,
}) => {
  const [draft, setDraft] = useState<BillingAddressDraft | null>(null);
  const [valid, setValid] = useState(false);

  const canSubmit = valid && !isSubmitting && draft != null;

  return (
    <Dialog
      open={isOpen}
      onClose={() => fireEvent?.("close-billing-address-modal")}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        Edit Billing Details
        <IconButton
          onClick={() => fireEvent?.("close-billing-address-modal")}
          size="small"
          disabled={isSubmitting}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <BillingAddressForm
          key={isOpen ? "open" : "closed"}
          address={billingAddress}
          forceEdit
          onChange={setDraft}
          onValidityChange={setValid}
          language={language}
        />
        {errorMessage ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={() => fireEvent?.("close-billing-address-modal")}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={() => {
            if (!canSubmit || !draft) return;
            fireEvent?.("submit-billing-address", { billingAddress: draft });
          }}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
          sx={{
            bgcolor: "#ff8c00",
            color: "#fff",
            borderRadius: 2,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { bgcolor: "#e67e00" },
            "&.Mui-disabled": { bgcolor: "#f0d4b3", color: "#fff" },
          }}
        >
          {isSubmitting ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
